import { useState, useEffect } from "react";
import { Link, Routes, Route, useNavigate } from "react-router-dom";
import "./App.css";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Slider } from "./components/ui/slider";
import { Separator } from "./components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Flower2, TrendingUp, DollarSign, PiggyBank, BarChart3, Sprout, Shield, Globe, Award, Leaf, Droplet, Package, ArrowRight, ChevronRight, CheckCircle2, Target, Wallet, MapPin, AlertCircle, Layers } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import taybLogo from "./assets/tayb-logo.png";
import SignupForm from "./signup";

const SUPPORTED_CURRENCIES = ['NZD', 'USD', 'GBP', 'SGD', 'AUD', 'AED'];

function App() {
  const [mode, setMode] = useState('long'); // 'short' or 'long'
  const [lambCount, setLambCount] = useState(100);
  const [years, setYears] = useState(5);
  const [results, setResults] = useState(null);
  const [currency, setCurrency] = useState('NZD');
  const [rates, setRates] = useState({ NZD: 1 }); // NZD as base
  const [ratesLoading, setRatesLoading] = useState(true);

  // Fetch exchange rates (NZD base) once on mount
  useEffect(() => {
    fetch('https://open.er-api.com/v6/latest/NZD')
      .then((r) => r.json())
      .then((data) => {
        if (data && data.rates) {
          const filtered = { NZD: 1 };
          SUPPORTED_CURRENCIES.forEach((c) => {
            if (data.rates[c] !== undefined) filtered[c] = data.rates[c];
          });
          setRates(filtered);
        }
      })
      .catch(() => {
        // Fallback static rates (approximate, used only if API fails)
        setRates({ NZD: 1, USD: 0.60, GBP: 0.47, SGD: 0.81, AUD: 0.92, AED: 2.20 });
      })
      .finally(() => setRatesLoading(false));
  }, []);

  // Simulation logic - matches Excel calculation exactly
  const calculateInvestment = (initialLambs, investmentYears, fixedCycles = null) => {
    const LAMB_COST = 220;
    const PLATFORM_FEE = 20;
    const COST_PER_LAMB = LAMB_COST + PLATFORM_FEE; // $240
    const RETURN_PER_CYCLE = 281; // $220 capital + $61 profit
    const PROFIT_PER_CYCLE = 61;

    const cycles = fixedCycles !== null ? fixedCycles : investmentYears * 2;
    let currentLambs = initialLambs;
    let cash = 0; // Idle cash from previous reinvestment
    let totalFees = initialLambs * PLATFORM_FEE; // Initial purchase fees
    let totalLambsSold = 0; // Cumulative lambs reared and sold across all cycles

    const yearlyData = [];
    const cycleData = [];

    // Initial investment
    const initialInvestment = initialLambs * COST_PER_LAMB;

    let yearLambs = 0; // lambs sold within current year (resets each year)

    for (let cycle = 1; cycle <= cycles; cycle++) {
      // Returns from current lamb count this cycle
      const cycleReturns = currentLambs * RETURN_PER_CYCLE;

      // These lambs complete the cycle and are sold - add to cumulative counts
      totalLambsSold += currentLambs;
      yearLambs += currentLambs;

      // Total cash position if liquidated at end of this cycle
      // = previous idle cash + this cycle's returns
      const totalCashAtCycle = cash + cycleReturns;
      const cycleProfit = totalCashAtCycle - initialInvestment;

      // Store cycle data: state during this cycle
      cycleData.push({
        cycle: cycle,
        year: Math.ceil(cycle / 2),
        lambCount: currentLambs,
        portfolioValue: totalCashAtCycle,
        idleCash: cash
      });

      // Store yearly data (every 2 cycles) - lambCount is sum of both cycles in this year
      if (cycle % 2 === 0) {
        const year = cycle / 2;
        yearlyData.push({
          year: year,
          lambCount: yearLambs,
          herdCount: currentLambs,
          portfolioValue: totalCashAtCycle,
          profit: cycleProfit,
          idleCash: cash,
          fees: totalFees
        });
        yearLambs = 0; // reset for next year
      }

      // Reinvest only if not the last cycle
      // Cash is NOT carried forward - each cycle's reinvestment uses only that cycle's returns
      if (cycle < cycles) {
        const newLambs = Math.floor(cycleReturns / COST_PER_LAMB);
        cash = cycleReturns % COST_PER_LAMB;

        // Add platform fees for new lambs purchased
        totalFees += newLambs * PLATFORM_FEE;

        // Update for next cycle
        currentLambs = newLambs;
      }
      // On last cycle: don't reinvest, keep currentLambs and previous cash as-is
    }

    // Final state: lambs that operated in last cycle + (previous idle cash + last cycle returns)
    const finalLambCount = currentLambs;
    const finalCycleReturns = currentLambs * RETURN_PER_CYCLE;
    const finalPortfolioValue = cash + finalCycleReturns;
    const finalIdleCash = cash;
    const totalProfit = finalPortfolioValue - initialInvestment;
    const roi = (totalProfit / initialInvestment * 100).toFixed(2);
    const growthMultiple = (finalPortfolioValue / initialInvestment).toFixed(2);

    return {
      finalLambCount: finalLambCount,
      totalLambsSold: totalLambsSold,
      portfolioValue: finalPortfolioValue,
      idleCash: finalIdleCash,
      totalProfit: totalProfit,
      roi: roi,
      totalFees: totalFees,
      growthMultiple: growthMultiple,
      initialInvestment: initialInvestment,
      yearlyData: yearlyData,
      cycleData: cycleData
    };
  };

  useEffect(() => {
    const result = mode === 'short' ?
    calculateInvestment(lambCount, 0, 1) :
    calculateInvestment(lambCount, years);
    setResults(result);
  }, [lambCount, years, mode]);

  const formatCurrency = (value) => {
    const rate = rates[currency] || 1;
    const converted = value * rate;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(converted);
  };

  // Plain number with thousands separator (no currency symbol) - converted to selected currency
  const formatNumber = (value) => {
    const rate = rates[currency] || 1;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value * rate);
  };

  return (
    <div className="App">
      {/* Premium Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-[#e8e3d6] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="tayb-logo">
            <img src={taybLogo} alt="TAYB" className="h-12 w-auto" style={{mixBlendMode: 'multiply'}} />
            <div className="hidden sm:block h-8 w-px bg-[#d4cdb8]"></div>
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-[10px] tracking-[0.3em] text-[#a8a496] uppercase">Pure · Ethical · Halal</span>
              <span className="text-[10px] tracking-widest text-[#5f7161] uppercase mt-0.5">Ethical Investment</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[#3c4b3c]">
            <a href="#how-it-works" className="hover:text-[#5f7161] font-medium transition-colors">How It Works</a>
            <a href="#paths" className="hover:text-[#5f7161] font-medium transition-colors">Investment Paths</a>
            <a href="#simulator" className="hover:text-[#5f7161] font-medium transition-colors">Simulator</a>
          </nav>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="w-[110px] border-[#d4cdb8] text-[#3c4b3c] font-semibold bg-[#faf9f5]" data-testid="currency-selector">
              <SelectValue placeholder="Currency" />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_CURRENCIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="hero-bg relative overflow-hidden">
        {/* Subtle animated overlay shapes */}
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute bottom-0 left-0 w-full h-32 opacity-20 floating-line" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,80 Q300,40 600,60 T1200,50 L1200,120 L0,120 Z" fill="rgba(247,246,242,0.4)" />
          </svg>
          <svg className="absolute top-1/4 right-0 w-1/2 h-64 opacity-10 floating-line" viewBox="0 0 600 200" preserveAspectRatio="none" style={{animationDelay: '2s'}}>
            <polyline points="0,150 80,130 160,140 240,100 320,110 400,70 480,80 560,40 600,50" fill="none" stroke="#f7f6f2" strokeWidth="2" />
          </svg>
        </div>

        <div className="container mx-auto px-6 py-24 md:py-32 relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full glass-card text-[#f7f6f2] text-xs tracking-widest uppercase">
              <Shield className="w-3.5 h-3.5" />
              Shari'a-Compliant · Asset Backed · NAIT Registered
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-white leading-[1.05] mb-6">
              Build Long-Term Wealth Through <em className="text-[#d4e4c8] not-italic font-normal italic">Shari'a-Compliant</em> Livestock Investment
            </h1>
            <p className="text-base md:text-lg text-[#e8e8e0] max-w-2xl leading-relaxed mb-10">
              Invest in professionally managed lamb livestock cycles in New Zealand through a Shari'a-compliant Mudarabah (profit-sharing) structure, with each livestock unit individually tracked and registered under your name through the national NAIT traceability system. Designed as a low-risk, high-liquidity, asset-backed investment programme focused on ethical wealth building and long-term portfolio growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="signup.html" data-testid="cta-sign-up"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#f7f6f2] text-[#3c4b3c] font-semibold rounded-full hover:bg-white transition-all shadow-xl">
                Sign Up to Invest <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#how-it-works" data-testid="cta-learn-how"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/40 text-white font-semibold rounded-full hover:bg-white/10 backdrop-blur transition-all">
                Learn How It Works
              </a>
            </div>
          </div>
        </div>

        {/* Trust indicator pills */}
        <div className="container mx-auto px-6 pb-12 relative">
          <div className="flex flex-wrap gap-3 justify-start max-w-5xl">
            {[
              { icon: Shield, label: "Shari'a Compliant" },
              { icon: MapPin, label: "NAIT Registered" },
              { icon: Package, label: "Real Asset Backed" },
              { icon: Globe, label: "New Zealand Based" },
              { icon: Droplet, label: "High Liquidity" },
              { icon: Leaf, label: "Ethical Investment" }
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="trust-pill flex items-center gap-2 px-4 py-2 rounded-full text-white text-xs sm:text-sm font-medium">
                <Icon className="w-4 h-4 text-[#d4e4c8]" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 1 — HOW IT WORKS */}
      <section id="how-it-works" className="py-24 bg-[#f7f6f2]">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-xs tracking-[0.3em] text-[#5f7161] uppercase mb-3">The Investment Lifecycle</div>
            <h2 className="font-display text-4xl md:text-5xl font-medium text-[#3c4b3c] mb-4">How the Investment Cycle Works</h2>
            <p className="text-[#5f7161] text-base md:text-lg leading-relaxed">
              A transparent, asset-backed investment experience supported by New Zealand's regulated livestock ecosystem.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4 max-w-7xl mx-auto">
            {/* Step 1 */}
            <div className="timeline-step bg-white rounded-2xl p-7 border border-[#e8e3d6]">
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-xl bg-[#f0ede4] flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-[#5f7161]" />
                </div>
                <span className="text-xs tracking-widest text-[#a8a496] font-semibold">STEP 01</span>
              </div>
              <h3 className="font-display text-2xl font-semibold text-[#3c4b3c] mb-3">Livestock allocation</h3>
              <p className="text-sm text-[#5f7161] leading-relaxed mb-4">
                Each lamb cycle is allocated under your name with a fixed entry cost. Full transparency from day one.
              </p>
              <p className="text-sm text-[#5f7161] leading-relaxed">
                The cost will include the livestock purchase price and a fixed platform fee per cycle. See the simulator below for details.
              </p>
            </div>

            {/* Step 2 */}
            <div className="timeline-step bg-white rounded-2xl p-7 border border-[#e8e3d6]">
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-xl bg-[#f0ede4] flex items-center justify-center">
                  <Sprout className="w-6 h-6 text-[#5f7161]" />
                </div>
                <span className="text-xs tracking-widest text-[#a8a496] font-semibold">STEP 02</span>
              </div>
              <h3 className="font-display text-2xl font-semibold text-[#3c4b3c] mb-3">Professional Management</h3>
              <p className="text-sm text-[#5f7161] leading-relaxed mb-4">
                Lambs are professionally managed through a 6-month cycle in New Zealand by government-accredited farmers.
              </p>
              <ul className="space-y-2.5 text-sm text-[#3c4b3c]">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#5f7161] mt-0.5 flex-shrink-0" /><span>NAIT-tracked location visibility in your portal</span></li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#5f7161] mt-0.5 flex-shrink-0" /><span>Insured throughout the cycle for capital protection</span></li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="timeline-step bg-white rounded-2xl p-7 border border-[#e8e3d6]">
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-xl bg-[#f0ede4] flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-[#5f7161]" />
                </div>
                <span className="text-xs tracking-widest text-[#a8a496] font-semibold">STEP 03</span>
              </div>
              <h3 className="font-display text-2xl font-semibold text-[#3c4b3c] mb-3">Cycle Completion</h3>
              <p className="text-sm text-[#5f7161] leading-relaxed mb-4">
                Settlement at regulated abattoirs with official NAIT records reflected in your portal.
              </p>
              <ul className="space-y-2.5 text-sm text-[#3c4b3c]">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#5f7161] mt-0.5 flex-shrink-0" /><span>Shari'a-compliant processing, GCC export markets</span></li>
              </ul>
            </div>

            {/* Step 4 */}
            <div className="timeline-step bg-gradient-to-br from-[#3c4b3c] to-[#5f7161] rounded-2xl p-7 text-white">
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
                  <Target className="w-6 h-6 text-[#d4e4c8]" />
                </div>
                <span className="text-xs tracking-widest text-white/60 font-semibold">STEP 04</span>
              </div>
              <h3 className="font-display text-2xl font-semibold text-white mb-3">Growth Cycle</h3>
              <p className="text-sm text-white/90 leading-relaxed">
                Profit split 50/50 with the farmer. Capital returned upon completion or reinvested automatically.
              </p>
            </div>
          </div>

          <p className="text-center text-sm md:text-base text-[#5f7161] italic mt-10 max-w-3xl mx-auto leading-relaxed">
            All cycle outcomes are transparently recorded and traceable, providing investors with verified, data-backed settlement information at the end of each investment cycle.
          </p>
        </div>
      </section>

      {/* SECTION 2 — INVESTMENT PATH COMPARISON */}
      <section id="paths" className="py-24 bg-gradient-to-b from-[#f7f6f2] to-[#efece2]">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-xs tracking-[0.3em] text-[#5f7161] uppercase mb-3">Two Paths · One Asset</div>
            <h2 className="font-display text-4xl md:text-5xl font-medium text-[#3c4b3c] mb-4">Choose Your Investment Path</h2>
            <p className="text-[#5f7161] text-base md:text-lg leading-relaxed">
              Whether you're seeking liquidity-friendly returns or compounding wealth over time, both paths share the same underlying real asset.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Single Cycle Card */}
            <div className="premium-card rounded-3xl p-10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-[#f0ede4] flex items-center justify-center">
                  <Layers className="w-6 h-6 text-[#5f7161]" />
                </div>
                <span className="text-xs tracking-widest text-[#a8a496] uppercase font-semibold">Path 01</span>
              </div>
              <h3 className="font-display text-3xl font-semibold text-[#3c4b3c] mt-4 mb-1">Single Cycle</h3>
              <p className="text-[#5f7161] text-sm mb-6">6-Month Investment Cycle</p>
              <div className="border-t border-[#e8e3d6] pt-6 space-y-3.5">
                {[
                  "Returns paid after one completed cycle",
                  "Flexible liquidity at every settlement",
                  "Suitable for shorter-term objectives",
                  "No automatic reinvestment"
                ].map((f) => (
                  <div key={f} className="flex items-start gap-3 text-sm text-[#3c4b3c]">
                    <CheckCircle2 className="w-5 h-5 text-[#5f7161] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => { setMode('short'); document.getElementById('simulator').scrollIntoView({ behavior: 'smooth' }); }}
                data-testid="path-short-cta"
                className="mt-8 w-full py-3.5 border border-[#5f7161] text-[#3c4b3c] font-semibold rounded-full hover:bg-[#5f7161] hover:text-white transition-all">
                Simulate Single Cycle
              </button>
            </div>

            {/* Wealth Builder Card */}
            <div className="rounded-3xl p-10 relative overflow-hidden bg-gradient-to-br from-[#3c4b3c] to-[#5f7161] text-white shadow-2xl" style={{transition: 'all 0.4s ease'}}>
              <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-[#d4e4c8] text-[#3c4b3c] text-[10px] tracking-widest uppercase font-bold">Recommended</div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#d4e4c8]" />
                </div>
                <span className="text-xs tracking-widest text-white/70 uppercase font-semibold">Path 02</span>
              </div>
              <h3 className="font-display text-3xl font-semibold text-white mt-4 mb-1">Wealth Builder</h3>
              <p className="text-white/70 text-sm mb-6">Long-Term Compounding Strategy</p>
              <div className="border-t border-white/15 pt-6 space-y-3.5">
                {[
                  "Automated reinvestment after each cycle",
                  "Portfolio expansion over time",
                  "Designed for long-term wealth building",
                  "Withdraw partially or fully after any cycle — no penalty, subject to settlement"
                ].map((f) => (
                  <div key={f} className="flex items-start gap-3 text-sm text-white/95">
                    <CheckCircle2 className="w-5 h-5 text-[#d4e4c8] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => { setMode('long'); document.getElementById('simulator').scrollIntoView({ behavior: 'smooth' }); }}
                data-testid="path-long-cta"
                className="mt-8 w-full py-3.5 bg-[#f7f6f2] text-[#3c4b3c] font-semibold rounded-full hover:bg-white transition-all">
                Simulate Wealth Builder
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — INVESTMENT SIMULATOR */}
      <section id="simulator" className="py-24 bg-[#f7f6f2]">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-xs tracking-[0.3em] text-[#5f7161] uppercase mb-3">Interactive Tool</div>
            <h2 className="font-display text-4xl md:text-5xl font-medium text-[#3c4b3c] mb-4">Investment Simulator</h2>
            <p className="text-[#5f7161] text-base md:text-lg leading-relaxed">Discover how your lamb investment could grow over time with our automated reinvestment simulator. Choose between a single-cycle investment for short-term returns or a long-term reinvestment strategy designed for wealth building.</p>
            <p className="mt-5 text-xs md:text-sm text-[#a8a496] italic leading-relaxed max-w-2xl mx-auto">
              These simulations are based on past performance and industry benchmarks. Past performance does not guarantee future results. Actual profits and returns may vary depending on market conditions and other factors.
            </p>
          </div>

      <div className="container mx-auto px-4">
        <div className="hidden">
        {/* legacy hero - kept hidden as placeholder for unchanged JSX below */}
          <h2 className="text-4xl md:text-5xl font-bold text-[#8b4513] mb-4">Investment simulation

          </h2>
          <p className="text-lg text-[#6b4423] max-w-2xl mx-auto">Discover how your lamb investment could grow over time with our automated reinvestment simulator.
Choose between a single-cycle investment for short-term returns or a long-term reinvestment strategy designed for wealth building.
          </p>
          <p className="text-xs text-[#8b4513]/70 italic max-w-3xl mx-auto mt-4 px-4">
            <strong>Disclaimer:</strong> These simulations are based on past performance and industry benchmarks. Past performance does not guarantee future results. Actual profits and returns may vary depending on market conditions and other factors.
          </p>
        </div>

        {/* Mode Selection */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 max-w-3xl mx-auto" data-testid="mode-selector">
          <button
            onClick={() => setMode('short')}
            data-testid="mode-short-btn"
            className={`flex-1 px-6 py-5 rounded-2xl border transition-all text-left ${
            mode === 'short' ?
            'bg-gradient-to-br from-[#3c4b3c] to-[#5f7161] text-white border-transparent shadow-lg' :
            'bg-white text-[#3c4b3c] border-[#e8e3d6] hover:border-[#5f7161]'}`
            }>
            <div className="font-display text-xl font-semibold mb-1">Single Cycle</div>
            <div className={`text-sm ${mode === 'short' ? 'text-white/80' : 'text-[#5f7161]'}`}>
              Single cycle (6 months) — per cycle returns
            </div>
          </button>
          <button
            onClick={() => setMode('long')}
            data-testid="mode-long-btn"
            className={`flex-1 px-6 py-5 rounded-2xl border transition-all text-left ${
            mode === 'long' ?
            'bg-gradient-to-br from-[#3c4b3c] to-[#5f7161] text-white border-transparent shadow-lg' :
            'bg-white text-[#3c4b3c] border-[#e8e3d6] hover:border-[#5f7161]'}`
            }>
            <div className="font-display text-xl font-semibold mb-1">Wealth Builder</div>
            <div className={`text-sm ${mode === 'long' ? 'text-white/80' : 'text-[#5f7161]'}`}>
              Wealth creation with auto-reinvestment (1–10 years)
            </div>
          </button>
        </div>

        {/* Simulator Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Input Controls */}
          <Card className="border border-[#e8e3d6] shadow-sm bg-white rounded-2xl overflow-hidden">
            <CardHeader className="bg-gradient-to-br from-[#faf9f5] to-[#f0ede4] border-b border-[#e8e3d6]">
              <CardTitle className="font-display text-2xl text-[#3c4b3c] flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#5f7161]" />
                Investment Parameters
              </CardTitle>
              <CardDescription className="text-[#5f7161]">
                {mode === 'short' ? 'Adjust your lamb count for a single 6-month cycle' : 'Adjust your initial investment and time horizon'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-8">
              {/* Lamb Count Slider */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-semibold text-[#3c4b3c] tracking-wide">Initial Lamb Count</label>
                  <div className="flex items-center gap-2 bg-[#faf9f5] border border-[#e8e3d6] px-4 py-2 rounded-full">
                    <Flower2 className="w-4 h-4 text-[#5f7161]" />
                    <span className="text-2xl font-bold text-[#3c4b3c] font-display">{lambCount}</span>
                  </div>
                </div>
                <Slider
                  value={[lambCount]}
                  onValueChange={(value) => setLambCount(value[0])}
                  min={10}
                  max={5000}
                  step={10}
                  className="slider-track" />

                <div className="flex justify-between text-xs text-[#a8a496]">
                  <span>Min: 10</span>
                  <span>Max: 5,000</span>
                </div>
              </div>

              {/* Years Slider - only for long-term mode */}
              {mode === 'long' &&
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-semibold text-[#3c4b3c] tracking-wide">Investment Duration</label>
                  <div className="flex items-center gap-2 bg-[#faf9f5] border border-[#e8e3d6] px-4 py-2 rounded-full">
                    <TrendingUp className="w-4 h-4 text-[#5f7161]" />
                    <span className="text-2xl font-bold text-[#3c4b3c] font-display">{years}</span>
                    <span className="text-sm text-[#5f7161]">years</span>
                  </div>
                </div>
                <Slider
                  value={[years]}
                  onValueChange={(value) => setYears(value[0])}
                  min={1}
                  max={10}
                  step={1}
                  className="slider-track" />

                <div className="flex justify-between text-xs text-[#a8a496]">
                  <span>1 Year</span>
                  <span>10 Years</span>
                </div>
              </div>
              }

              {/* Cycle indicator - only for short-term mode */}
              {mode === 'short' &&
              <div className="space-y-2 bg-[#faf9f5] p-4 rounded-2xl border border-[#e8e3d6]">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-semibold text-[#3c4b3c] tracking-wide">Investment Duration</label>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#5f7161]" />
                    <span className="text-lg font-bold text-[#3c4b3c] font-display">1 cycle</span>
                    <span className="text-sm text-[#5f7161]">(6 months)</span>
                  </div>
                </div>
                <p className="text-xs text-[#a8a496]">Fixed to a single cycle. No reinvestment.</p>
              </div>
              }

              {/* Initial Investment Display */}
              <div className="bg-gradient-to-br from-[#3c4b3c] to-[#5f7161] p-5 rounded-2xl text-white">
                <p className="text-xs tracking-[0.2em] text-white/70 uppercase mb-1">Initial Investment Required ({currency})</p>
                <p className="font-display text-3xl font-semibold">{results && formatNumber(results.initialInvestment)}</p>
                <p className="text-xs text-white/70 mt-2">= {lambCount} lambs × {formatNumber(240)} ({formatNumber(220)} purchase + {formatNumber(20)} fee)</p>
              </div>

              <a href="signup.html"
                data-testid="cta-sign-up-params"
                className="inline-flex w-full items-center justify-center gap-2 px-7 py-3.5 bg-[#3c4b3c] text-white font-semibold rounded-full hover:bg-[#2d3a2d] transition-all shadow-md">
                Sign Up to Invest <ArrowRight className="w-4 h-4" />
              </a>
            </CardContent>
          </Card>

          {/* Key Results */}
          <div className="space-y-4">
            <Card className="border border-[#e8e3d6] shadow-sm stat-card bg-white rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-br from-[#3c4b3c] to-[#5f7161] text-white">
                <CardTitle className="text-xs tracking-[0.25em] uppercase font-semibold text-white/80 flex items-center gap-2">
                  <PiggyBank className="w-4 h-4 text-[#d4e4c8]" />
                  Final Portfolio Value ({currency})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="font-display text-5xl font-semibold text-[#3c4b3c]">{results && formatNumber(results.portfolioValue)}</p>
                <p className="text-sm text-[#5f7161] mt-2">{mode === 'short' ? 'After 1 cycle (6 months)' : `After ${years} years of growth`}</p>
              </CardContent>
            </Card>

            <Card className="border border-[#e8e3d6] shadow-sm stat-card bg-white rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-br from-[#faf9f5] to-[#f0ede4] border-b border-[#e8e3d6]">
                <CardTitle className="text-xs tracking-[0.25em] uppercase font-semibold text-[#5f7161] flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#5f7161]" />
                  Total Profit ({currency})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="font-display text-4xl font-semibold text-[#3c4b3c]">{results && formatNumber(results.totalProfit)}</p>
                <p className="text-sm text-[#5f7161] mt-2">ROI: <span className="font-semibold text-[#3c4b3c]">{results && results.roi}%</span></p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              {mode === 'long' &&
              <Card className="border border-[#e8e3d6] stat-card bg-white rounded-2xl">
                <CardContent className="pt-6">
                  <p className="text-xs tracking-[0.2em] uppercase text-[#5f7161] mb-2">Sales (# of items)</p>
                  <p className="font-display text-2xl font-semibold text-[#3c4b3c] flex items-center gap-2">
                    <Flower2 className="w-5 h-5 text-[#5f7161]" />
                    {results && results.totalLambsSold.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              }

              {mode === 'long' &&
              <Card className="border border-[#e8e3d6] stat-card bg-white rounded-2xl">
                <CardContent className="pt-6">
                  <p className="text-xs tracking-[0.2em] uppercase text-[#5f7161] mb-2">Growth Multiple</p>
                  <p className="font-display text-2xl font-semibold text-[#3c4b3c]">{results && results.growthMultiple}<span className="text-[#5f7161] text-lg ml-1">x</span></p>
                </CardContent>
              </Card>
              }

              {mode === 'long' &&
              <Card className="border border-[#e8e3d6] stat-card bg-white rounded-2xl">
                <CardContent className="pt-6">
                  <p className="text-xs tracking-[0.2em] uppercase text-[#5f7161] mb-2">Total Platform Fees Paid ({currency})</p>
                  <p className="font-display text-2xl font-semibold text-[#3c4b3c]">{results && formatNumber(results.totalFees)}</p>
                </CardContent>
              </Card>
              }

              {mode === 'short' && (
              <Card className="border border-[#e8e3d6] stat-card col-span-2 bg-white rounded-2xl">
                <CardContent className="pt-6">
                  <p className="text-xs tracking-[0.2em] uppercase text-[#5f7161] mb-2">Total Platform Fees Paid ({currency})</p>
                  <p className="font-display text-2xl font-semibold text-[#3c4b3c]">{results && formatNumber(results.totalFees)}</p>
                </CardContent>
              </Card>
              )}
            </div>
          </div>
        </div>

        {mode === 'long' && <>
        </>}
      </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3c4b3c] text-white py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4" data-testid="footer-logo">
              <img src={taybLogo} alt="TAYB" className="h-14 w-auto" style={{filter: 'brightness(0) invert(0.9) sepia(0.3) saturate(2) hue-rotate(60deg)'}} />
              <div className="h-10 w-px bg-white/20"></div>
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] tracking-[0.3em] text-white/60 uppercase">Pure · Ethical · Halal</span>
                <span className="font-display text-sm text-white mt-0.5">Ethical Investment</span>
              </div>
            </div>
            <p className="text-white/70 text-xs text-center md:text-right max-w-md">
              Sustainable, Shari'a-compliant livestock investment platform. All calculations are projections based on the stated business model.
              <br/>© 2025 TAYB Limited.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;